/**
 * Vista de Ajustes, Sincronizacion e Integridad de FinVoz (FR-STORE-1..13, spec/05-security.md).
 *
 * Muestra:
 *  - Estado de conexion Google OAuth PKCE (token solo en memoria, ADR OQ-2)
 *  - Vinculacion con Google Sheets (sistema autoritativo de registro, ADR OQ-1)
 *  - Cola de hechos confirmados pendientes de acuse (Outbox, FR-STORE-10)
 *  - Reconstruccion sin perdida desde Sheets (FR-SYNC-7, FR-SYNC-9)
 *  - Registro de integridad y visor de cuarentena (FR-STORE-12, FR-STORE-13)
 *  - Instalacion de PWA (FR-SYNC-8)
 *  - Cierre de sesion con purga total local (FR-STORE-8)
 */

import type { AppState } from "../types.js";
import { escapeHtml, escapeAttr } from "../sanitize.js";

export function renderSettings(state: AppState): string {
  const isLinked = Boolean(state.spreadsheetId);
  const isGoogleConnected = Boolean(state.googleUserEmail);
  const preview = state.pendingImportPreview;

  return `
    <div class="settings-view" data-testid="settings-view">
      <!-- PERFILES LOCALES (revisión de ADR OQ-1, 2026-09-10): IndexedDB local es la autoridad -->
      <div class="card" data-testid="profiles-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Perfiles Locales</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              IndexedDB en este dispositivo es la fuente de autoridad. Cada perfil es una partición de datos aislada — sin usuario online ni contraseña de servidor.
            </div>
          </div>
          <button class="btn-primary" data-testid="new-profile-btn" data-action="open-new-profile-modal">
            + Nuevo Perfil
          </button>
        </div>
        <div style="display: flex; flex-direction: column; gap: 8px;">
          ${state.profiles
            .map(
              (p) => `
            <div
              data-testid="profile-row"
              data-profile-id="${escapeAttr(p.profileId)}"
              style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border: 1px solid var(--border); border-radius: 8px; ${p.profileId === state.activeProfileId ? "background-color: var(--bg-tertiary); border-color: var(--accent);" : ""}"
            >
              <div>
                <strong>${escapeHtml(p.name)}</strong>
                ${p.profileId === state.activeProfileId ? '<span class="badge badge-voice" data-testid="active-profile-badge" style="margin-left: 8px;">Activo</span>' : ""}
              </div>
              <div style="display: flex; gap: 8px;">
                ${
                  p.profileId !== state.activeProfileId
                    ? `<button class="btn-secondary" data-testid="select-profile-btn" data-action="select-profile" data-profile-id="${escapeAttr(p.profileId)}">Seleccionar</button>`
                    : ""
                }
                ${
                  state.profiles.length > 1
                    ? `<button class="btn-danger" data-testid="delete-profile-btn" data-action="delete-profile" data-profile-id="${escapeAttr(p.profileId)}">Eliminar</button>`
                    : ""
                }
              </div>
            </div>
          `,
            )
            .join("")}
        </div>
      </div>

      <!-- MODAL: NUEVO PERFIL -->
      <div id="new-profile-modal" class="modal-overlay" style="display: none;">
        <div class="modal-content">
          <h3>Nuevo Perfil Local</h3>
          <form data-form="new-profile">
            <label for="new-profile-name">Nombre del perfil:</label>
            <input type="text" id="new-profile-name" data-testid="new-profile-name-input" placeholder="ej: Negocio, Hogar, Viaje" required />
            <div style="display: flex; gap: 8px; margin-top: 16px;">
              <button type="submit" class="btn-primary" data-testid="confirm-new-profile-btn">Crear Perfil</button>
              <button type="button" class="btn-secondary" data-action="close-new-profile-modal">Cancelar</button>
            </div>
          </form>
        </div>
      </div>

      <!-- COPIA DE SEGURIDAD PORTÁTIL .finvoz.json -->
      <div class="card" data-testid="backup-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Copia de Seguridad Portátil</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Archivo <code>.finvoz.json</code> con checksum, sin tokens ni secretos. Portabilidad entre dispositivos mientras no exista sincronización automática.
            </div>
          </div>
        </div>
        <div style="display: flex; gap: 10px; flex-wrap: wrap;">
          <button class="btn-primary" data-testid="export-backup-btn" data-action="export-backup">
            ⬇️ Exportar Copia de Seguridad
          </button>
          <button class="btn-secondary" data-testid="open-import-backup-btn" data-action="open-import-backup-modal">
            ⬆️ Importar Copia de Seguridad
          </button>
        </div>
      </div>

      <!-- MODAL: IMPORTAR BACKUP -->
      <div id="import-backup-modal" class="modal-overlay" style="display: ${state.importBackupModalOpen ? "flex" : "none"};">
        <div class="modal-content">
          <h3>Importar Copia de Seguridad</h3>
          <input type="file" id="import-backup-file-input" data-testid="import-backup-file-input" accept=".json,.finvoz.json" />
          ${
            preview
              ? preview.error
                ? `<div data-testid="import-backup-error" style="color: var(--danger); margin-top: 12px;">${escapeHtml(preview.error)}</div>`
                : `
                <div data-testid="import-backup-preview" style="margin-top: 12px; font-size: 0.85rem;">
                  <div>Perfil de origen: <strong>${escapeHtml(preview.profileName)}</strong></div>
                  <div>Exportado: ${escapeHtml(preview.exportedAt)}</div>
                  <div>Cuentas: ${preview.accountsCount} · Movimientos: ${preview.entriesCount} · Presupuestos: ${preview.budgetsCount} · Metas: ${preview.goalsCount} · Cotizaciones: ${preview.priceQuotesCount}</div>
                  <div style="display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
                    <button class="btn-danger" data-testid="import-mode-replace-btn" data-action="confirm-import-backup" data-import-mode="replace">
                      Reemplazar Perfil Actual
                    </button>
                    <button class="btn-primary" data-testid="import-mode-merge-btn" data-action="confirm-import-backup" data-import-mode="merge">
                      Combinar Sin Duplicar
                    </button>
                    <button class="btn-secondary" data-testid="import-mode-new-profile-btn" data-action="confirm-import-backup" data-import-mode="new-profile">
                      Crear Perfil Nuevo
                    </button>
                  </div>
                </div>
              `
              : ""
          }
          <div style="margin-top: 16px;">
            <button type="button" class="btn-secondary" data-action="close-import-backup-modal">Cerrar</button>
          </div>
        </div>
      </div>

      <!-- EXPORTACIÓN A GOOGLE SHEETS, NO AUTORITATIVA (revisión de ADR OQ-1, 2026-09-10) -->
      <div class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Exportación a Google Sheets</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Opcional y visual. IndexedDB local es la autoridad; Sheets no participa en cálculos ni en sincronización automática.
            </div>
          </div>
          <span class="badge ${isGoogleConnected ? 'badge-voice' : 'badge-manual'}">
            ${isGoogleConnected ? 'Google Conectado' : 'Modo Solo Local'}
          </span>
        </div>

        <!-- Autenticación Google OAuth PKCE -->
        <div style="background-color: var(--bg-tertiary); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="font-size: 0.95rem; font-weight: 600; margin-bottom: 8px;">1. Conexión OAuth PKCE (Cliente Público)</h3>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 12px;">
            FinVoz solicita el alcance mínimo sobre sus propios archivos (<code>drive.file</code>, FR-STORE-2).
            El token de acceso vive únicamente en memoria del proceso (INV-8, ADR OQ-2).
          </p>

          <div style="display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: end; margin-bottom: 12px;">
            <div>
              <label for="google-client-id" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">
                Google OAuth Client ID (Público):
              </label>
              <input
                type="text"
                id="google-client-id"
                data-testid="google-client-id-input"
                data-setting="google-client-id"
                style="width: 100%;"
                placeholder="ej: 123456789-abc.apps.googleusercontent.com"
                value="${escapeAttr(state.googleClientId || "")}"
              />
            </div>
            <button
              class="btn-primary"
              data-testid="connect-google-btn"
              data-action="connect-google"
            >
              ${isGoogleConnected ? '✓ Sesión Google Activa' : 'Iniciar sesión con Google'}
            </button>
          </div>
          ${isGoogleConnected ? `<div style="font-size: 0.8rem; color: var(--success);">Sesión activa en memoria: ${escapeHtml(state.googleUserEmail)}</div>` : ""}
        </div>

        <!-- Hoja de Cálculo en Drive -->
        <div style="background-color: var(--bg-tertiary); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
          <h3 style="font-size: 0.95rem; font-weight: 600; margin-bottom: 8px;">2. Hoja de Cálculo en Google Drive</h3>
          <div style="display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: end; margin-bottom: 12px;">
            <div>
              <label for="spreadsheet-id-input" style="font-size: 0.8rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">
                Spreadsheet ID de Google Sheets:
              </label>
              <input
                type="text"
                id="spreadsheet-id-input"
                data-testid="spreadsheet-id-input"
                data-setting="spreadsheet-id"
                style="width: 100%;"
                placeholder="ej: 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                value="${escapeAttr(state.spreadsheetId || "")}"
              />
            </div>
            <button class="btn-secondary" data-testid="link-sheet-btn" data-action="link-sheet">
              Vincular Hoja
            </button>
            <button class="btn-primary" data-testid="create-sheet-btn" data-action="create-sheet">
              Crear Nueva Hoja
            </button>
          </div>
          ${isLinked
            ? `<div style="font-size: 0.8rem; color: var(--accent);">Hoja vinculada: <a href="https://docs.google.com/spreadsheets/d/${encodeURIComponent(state.spreadsheetId || "")}" target="_blank" rel="noopener">Abrir en Google Sheets &nearr;</a></div>`
            : `<div style="font-size: 0.8rem; color: var(--text-muted);">Sin hoja vinculada. La aplicación opera como réplica durable local.</div>`
          }
        </div>

        <!-- Exportación manual, no autoritativa: Movimientos (histórico append-only) y Resumen (regenerable) -->
        <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--border); padding-top: 16px;">
          <div>
            <div style="font-weight: 600; font-size: 0.95rem;">Exportación Manual a la Hoja</div>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              "Movimientos" conserva todo el histórico (append-only, incluidas reversiones). "Resumen actual" se regenera con el saldo vigente y puede sustituirse sin afectar al histórico.
            </div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary" data-testid="export-resumen-btn" data-action="export-resumen">
              Regenerar Resumen Actual
            </button>
            <button class="btn-primary" data-testid="export-movimientos-btn" data-action="export-movimientos">
              Exportar Movimientos
            </button>
          </div>
        </div>
      </div>

      <!-- REGISTRO DE INTEGRIDAD Y CUARENTENA (ADR OQ-1 c.2, c.3; INV-9) -->
      <div class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Registro de Integridad y Cuarentena</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Cadenas independientes por dispositivo y detección de alteraciones externas (INV-9)
            </div>
          </div>
          <span class="badge ${state.quarantinedEntryIds.length === 0 ? 'status-online' : 'status-offline'}">
            ${state.quarantinedEntryIds.length === 0 ? 'Registro Íntegro' : `${state.quarantinedEntryIds.length} en Cuarentena`}
          </span>
        </div>

        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 16px;">
          Cada dispositivo encadena sus asientos con <code>device_seq</code> y <code>device_chain_hash</code>. Toda alteración o eliminación externa no coordinada se aísla automáticamente en cuarentena sin contaminar saldos ni sobrescribir datos (FR-STORE-6, FR-STORE-13).
        </p>

        ${state.quarantinedEntryIds.length > 0
          ? `
            <div style="background-color: var(--danger-bg); border: 1px solid var(--danger); border-radius: 8px; padding: 14px; margin-bottom: 16px;">
              <h4 style="color: var(--danger); font-size: 0.9rem; font-weight: 600; margin-bottom: 6px;">Asientos Aislados en Cuarentena:</h4>
              <ul style="padding-left: 20px; font-size: 0.85rem; font-family: var(--font-mono);">
                ${state.quarantinedEntryIds.map((id) => `<li>${escapeHtml(id)}</li>`).join("")}
              </ul>
              <div style="margin-top: 10px;">
                <button class="btn-secondary" style="font-size: 0.8rem;" data-action="dismiss-quarantine">
                  Revisar y Salir de Cuarentena
                </button>
              </div>
            </div>
          `
          : `<div style="font-size: 0.85rem; color: var(--success); margin-bottom: 16px;">✓ No hay asientos en cuarentena. La cadena local y remota coinciden.</div>`
        }
      </div>

      <!-- APARIENCIA Y TEMA VISUAL (WCAG 2.1 AA) -->
      <div class="card" data-testid="theme-settings-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Apariencia y Tema Visual</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Modo claro y oscuro con contraste accesible (WCAG 2.1 AA) y sincronización con el sistema
            </div>
          </div>
          <span class="badge badge-voice" data-testid="current-theme-badge">
            ${state.theme === "system" ? "🌓 Sistema" : state.theme === "light" ? "☀️ Modo Claro" : "🌙 Modo Oscuro"}
          </span>
        </div>

        <div style="display: flex; gap: 10px; flex-wrap: wrap;">
          <button
            class="${state.theme === 'system' ? 'btn-primary' : 'btn-secondary'}"
            data-action="set-theme"
            data-theme-val="system"
            data-testid="theme-btn-system"
          >
            🌓 Automático (Sistema)
          </button>
          <button
            class="${state.theme === 'light' ? 'btn-primary' : 'btn-secondary'}"
            data-action="set-theme"
            data-theme-val="light"
            data-testid="theme-btn-light"
          >
            ☀️ Modo Claro
          </button>
          <button
            class="${state.theme === 'dark' ? 'btn-primary' : 'btn-secondary'}"
            data-action="set-theme"
            data-theme-val="dark"
            data-testid="theme-btn-dark"
          >
            🌙 Modo Oscuro
          </button>
        </div>
      </div>

      <!-- DIAGNÓSTICO Y LABORATORIO DE VOZ -->
      <div class="card" data-testid="voice-diagnostics-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Diagnóstico y Calibración de Voz</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              Registro local privado de frases no reconocidas para evaluación y mejora del reconocedor (sin almacenar audio)
            </div>
          </div>
          <span class="badge" style="background-color: var(--bg-tertiary); color: var(--text-primary);" id="unrecognized-voice-count-badge">
            ${(typeof localStorage !== "undefined" ? JSON.parse(localStorage.getItem("finvoz_unrecognized_voice_logs") || "[]") : []).length} frases registradas
          </span>
        </div>

        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 12px;">
          FinVoz no graba ni transmite audio por motivos de privacidad (spec/05 §4.3). Puedes descargar este dataset de pruebas con el texto transcrito de los comandos no interpretados para auditar o calibrar el parser determinista.
        </p>

        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
          <button class="btn-secondary" data-action="export-voice-diagnostics" data-testid="export-voice-diagnostics-btn">
            📥 Descargar Dataset de Pruebas (.json)
          </button>
          <button class="btn-secondary" data-action="clear-voice-diagnostics" data-testid="clear-voice-diagnostics-btn" style="color: var(--danger);">
            🗑️ Limpiar Historial de Voz
          </button>
        </div>
      </div>

      <!-- DISPOSITIVO Y PWA -->
      <div class="card">
        <div class="card-header">
          <h2 class="card-title">Dispositivo y Aplicación PWA</h2>
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <div>
            <div style="font-weight: 500; font-size: 0.9rem;">Instalar Aplicación (PWA)</div>
            <div style="font-size: 0.8rem; color: var(--text-secondary);">
              Funciona sin conexión a internet y se instala como aplicación de escritorio o móvil (FR-SYNC-8).
            </div>
          </div>
          <button id="pwa-install-btn" class="btn-secondary" data-testid="pwa-install-btn" data-action="install-pwa">
            📲 Instalar PWA
          </button>
        </div>

        <div style="border-top: 1px solid var(--border); padding-top: 16px; display: flex; justify-content: space-between; align-items: center;">
          <div>
            <div style="font-weight: 600; color: var(--danger); font-size: 0.9rem;">Cerrar Sesión y Purgar Almacén Local</div>
            <div style="font-size: 0.8rem; color: var(--text-secondary);">
              Elimina todos los datos locales del dispositivo. La hoja de Google Sheets no se modifica (FR-STORE-8).
            </div>
          </div>
          <button class="btn-danger" data-testid="purge-local-btn" data-action="purge-local">
            🗑️ Purgar Datos Locales
          </button>
        </div>
      </div>
    </div>
  `;
}
